import React, { useState, useRef } from 'react';
import { useMutation, useQueryClient } from 'react-query';
import * as XLSX from 'xlsx';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { X, Upload, Download, FileText, AlertCircle, CheckCircle2 } from 'lucide-react';

const BulkUploadModal = ({ isOpen, onClose, onSuccess }) => {
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);
  const [file, setFile] = useState(null);
  const [errors, setErrors] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  const bulkUploadMutation = useMutation(
    async (products) => {
      const response = await api.post('/api/products/bulk', { products });
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success(data.message || 'Products uploaded successfully!');
        queryClient.invalidateQueries('products');
        onSuccess?.();
        handleClose();
      },
      onError: (error) => {
        const errorData = error.response?.data;
        if (errorData?.errors) {
          setErrors(errorData.errors);
          toast.error('Validation failed. Please check the errors below.');
        } else {
          toast.error(errorData?.message || 'Failed to upload products');
        }
      }
    }
  );

  const handleClose = () => {
    setFile(null);
    setErrors([]);
    setIsProcessing(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    onClose();
  };

  const downloadTemplate = () => {
    const template = [
      {
        'Item Name': 'Example Product',
        'Brand': 'Samsung',
        'Model': 'Galaxy S21',
        'Category Name': 'Mobile',
        'Part Number': 'PN12345',
        'Sale Rate': 50000,
        'Purchase Rate': 45000,
        'GST %': 18,
        'GST Inclusive': 'TRUE',
        'HSN Code': '8443',
        'Min Stock Limit': 5,
        'Opening Stock': 10,
        'Unit': 'PCS',
        'Description': 'Example description here'
      }
    ];

    const worksheet = XLSX.utils.json_to_sheet(template);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Template');
    
    // Auto-size columns
    const maxWidth = 20;
    worksheet['!cols'] = Object.keys(template[0]).map(() => ({ wch: maxWidth }));

    XLSX.writeFile(workbook, 'Product_Bulk_Upload_Template.xlsx');
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      if (!selectedFile.name.match(/\.(xlsx|xls)$/)) {
        toast.error('Please upload only Excel files (.xlsx or .xls)');
        return;
      }
      setFile(selectedFile);
      setErrors([]);
    }
  };

  const processUpload = async () => {
    if (!file) {
      toast.error('Please select a file first');
      return;
    }

    setIsProcessing(true);
    setErrors([]);

    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          toast.error('The Excel file is empty');
          setIsProcessing(false);
          return;
        }

        // Map Excel headers to API fields
        const mappedData = jsonData.map(row => ({
          name: row['Item Name'],
          brand: row['Brand'],
          model: row['Model'],
          categoryName: row['Category Name'],
          partNumber: row['Part Number'],
          saleRate: row['Sale Rate'],
          purchaseRate: row['Purchase Rate'],
          gstRate: row['GST %'],
          gstInclusive: String(row['GST Inclusive']).toUpperCase() === 'TRUE',
          hsnCode: row['HSN Code'],
          minStockLimit: row['Min Stock Limit'],
          openingStock: row['Opening Stock'],
          unit: row['Unit'],
          description: row['Description']
        }));

        bulkUploadMutation.mutate(mappedData);
      } catch (error) {
        console.error('Error parsing Excel:', error);
        toast.error('Failed to parse Excel file');
        setIsProcessing(false);
      }
    };
    reader.readAsArrayBuffer(file);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl overflow-hidden flex flex-col max-h-[90vh]">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Upload className="w-6 h-6" />
            <h2 className="text-xl font-bold">Bulk Product Upload</h2>
          </div>
          <button
            onClick={handleClose}
            className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="p-6 overflow-y-auto space-y-6">
          {/* Step 1: Download Template */}
          <div className="bg-blue-50 border border-blue-100 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <div className="bg-blue-100 p-2 rounded-lg">
                <FileText className="w-5 h-5 text-blue-600" />
              </div>
              <div className="flex-1">
                <h3 className="font-semibold text-blue-900">Step 1: Download Template</h3>
                <p className="text-sm text-blue-700 mt-1">
                  Download our Excel template to ensure your data is in the correct format.
                </p>
                <button
                  onClick={downloadTemplate}
                  className="mt-3 inline-flex items-center text-sm font-medium text-blue-700 hover:text-blue-800 bg-white border border-blue-200 px-3 py-1.5 rounded-md transition-colors"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download Template
                </button>
              </div>
            </div>
          </div>

          {/* Step 2: Upload File */}
          <div className="space-y-4">
            <h3 className="font-semibold text-gray-900">Step 2: Upload Filled File</h3>
            <div 
              onClick={() => fileInputRef.current?.click()}
              className={`border-2 border-dashed rounded-lg p-8 flex flex-col items-center justify-center cursor-pointer transition-all ${
                file ? 'border-green-300 bg-green-50' : 'border-gray-300 hover:border-blue-400 hover:bg-blue-50'
              }`}
            >
              <input
                type="file"
                ref={fileInputRef}
                onChange={handleFileChange}
                className="hidden"
                accept=".xlsx, .xls"
              />
              {file ? (
                <>
                  <CheckCircle2 className="w-12 h-12 text-green-500 mb-3" />
                  <p className="text-sm font-medium text-green-800">{file.name}</p>
                  <p className="text-xs text-green-600 mt-1">Click to change file</p>
                </>
              ) : (
                <>
                  <Upload className="w-12 h-12 text-gray-400 mb-3" />
                  <p className="text-sm font-medium text-gray-700">Click or drag Excel file here</p>
                  <p className="text-xs text-gray-500 mt-1">Supports .xlsx and .xls formats</p>
                </>
              )}
            </div>
          </div>

          {/* Errors Section */}
          {errors.length > 0 && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center space-x-2 text-red-800 mb-3">
                <AlertCircle className="w-5 h-5" />
                <h4 className="font-bold">Please fix the following errors in your Excel:</h4>
              </div>
              <ul className="space-y-1 max-h-40 overflow-y-auto pr-2">
                {errors.map((error, index) => (
                  <li key={index} className="text-sm text-red-700 flex items-start">
                    <span className="mr-2">•</span>
                    {error}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="bg-gray-50 px-6 py-4 flex items-center justify-end space-x-3 border-t">
          <button
            onClick={handleClose}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium"
          >
            Cancel
          </button>
          <button
            onClick={processUpload}
            disabled={!file || isProcessing || bulkUploadMutation.isLoading}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {isProcessing || bulkUploadMutation.isLoading ? (
              <>
                <div className="spinner w-4 h-4 mr-2"></div>
                Processing...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                Start Upload
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkUploadModal;
